import { AiMessageStatusEnum } from 'graphql-common';

// What the user can actually DO about a failed turn — drives the button on the
// error banner and, via AI_UNAVAILABLE_CODES below, whether the composer stays
// usable. Deliberately keyed on the remedy rather than on the error itself: two
// codes with the same fix share an action, and a code with no fix gets no
// button rather than one that leads nowhere.
export enum AiErrorAction {
  // The conversation is closed server-side and rejects every further message —
  // only a new chat can continue (guide §3).
  StartNewChat = 'start-new-chat',
  // The company is out of AI credits. Nothing in this modal helps; the plan
  // modal is the one route forward.
  UpgradePlan = 'upgrade-plan',
  // A transient failure — the same prompt is worth sending again as-is.
  Retry = 'retry',
  // No button. Either the fix is to rephrase (the prompt itself is the problem,
  // so re-sending it verbatim fails identically) or it needs an administrator.
  None = 'none',
}

// Codes that mean AI is unavailable account-wide rather than this turn having
// failed — the composer is locked while one is the last outcome, since the next
// send would fail the same way. Not conversation-scoped: starting a new chat
// does not clear these, which is why none of them offers StartNewChat.
const AI_UNAVAILABLE_CODES = [
  'budget_exhausted',
  'budget_not_configured',
  'plan_revoked',
  'access_revoked',
  'gate_failed',
];

// Whether `errorCode` means AI itself is off for the account, as opposed to this
// one turn having failed. Callers pair it with the live budget status — a mocked
// budget_exhausted reports this while the real budget is untouched, and a real
// exhaustion is visible in the credits even in a chat that never hit the error.
export function isAiUnavailableCode(errorCode?: string | null): boolean {
  return !!errorCode && AI_UNAVAILABLE_CODES.includes(errorCode);
}

// The one code the BE emits when a conversation overflows its context window.
const CONVERSATION_CLOSED_CODE = 'conversation_too_long';

// Whether a terminal turn outcome means the CONVERSATION is now closed
// server-side, not merely that this turn failed. The BE closes a conversation in
// exactly one place — the context-overflow rescue in Ai::SendMessageWorker —
// which finalizes the turn as TRUNCATED_BUDGET / conversation_too_long in the
// same transaction. So the outcome is an authoritative local stand-in for the
// `status: CLOSED` the conversations-list query only reports a round-trip later:
// callers that wait for that instead leave the composer inviting a message the
// conversation already rejects. Either signal alone suffices — the status
// survives a dropped error_code, and the code survives a status the FE has no
// mapping for.
export function isAiConversationClosedOutcome(
  status?: AiMessageStatusEnum | null,
  errorCode?: string | null,
): boolean {
  return (
    status === AiMessageStatusEnum.TruncatedBudget ||
    errorCode === CONVERSATION_CLOSED_CODE
  );
}

const ACTION_BY_CODE: Record<string, AiErrorAction> = {
  [CONVERSATION_CLOSED_CODE]: AiErrorAction.StartNewChat,
  budget_exhausted: AiErrorAction.UpgradePlan,
  // Transient by nature: the provider was busy, throttled, or briefly broken, so
  // the identical prompt is expected to work on a second attempt.
  rate_limited: AiErrorAction.Retry,
  server_error: AiErrorAction.Retry,
  configuration_error: AiErrorAction.Retry,
  completion_failed: AiErrorAction.Retry,
  // Deliberately no action. Re-sending is futile — the request needs rewording
  // (tool_call_limit_exceeded) or a document the user can no longer read
  // (attachment_unauthorized) — and no button beats a button that re-fails.
  tool_call_limit_exceeded: AiErrorAction.None,
  attachment_unauthorized: AiErrorAction.None,
  // The referenced document's file is gone from storage, so the turn cannot be
  // rebuilt as it stands — the copy tells the user to re-upload it (or drop it
  // from the conversation), which is the one code here they can act on
  // themselves, and neither is a button this banner can offer.
  file_missing: AiErrorAction.None,
  // An administrator has to act (configure a budget, restore access); the copy
  // already says so, so there is nothing to offer here.
  budget_not_configured: AiErrorAction.None,
  plan_revoked: AiErrorAction.None,
  access_revoked: AiErrorAction.None,
  gate_failed: AiErrorAction.None,
};

// The action offered for a terminal turn outcome. Pair with getAiMessageErrorText
// — a status/code with no user-facing copy renders no banner, so its action is
// never read.
export default function getAiErrorAction(
  status?: AiMessageStatusEnum | null,
  errorCode?: string | null,
): AiErrorAction {
  // TRUNCATED_BUDGET only ever means the context window overflowed and the BE
  // closed the conversation, so it maps by status — its row is worth trusting
  // even if the error_code did not survive.
  if (status === AiMessageStatusEnum.TruncatedBudget) {
    return AiErrorAction.StartNewChat;
  }
  if (errorCode && ACTION_BY_CODE[errorCode]) return ACTION_BY_CODE[errorCode];
  // A server-side cancel (guide §4) ends the turn with no answer and no reason
  // the user caused — worth another try. The client-side Stop renders no banner
  // at all (stopped_by_user is silent copy), so it never reaches this.
  if (status === AiMessageStatusEnum.Cancelled) return AiErrorAction.Retry;
  // Anything else failed for a reason the FE has no mapping for — it surfaces as
  // the generic error, which is retryable.
  if (status === AiMessageStatusEnum.Failed) return AiErrorAction.Retry;
  return AiErrorAction.None;
}
